import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDownRight, Landmark, ShieldCheck, Wallet } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

// Per-element reveal that fires once. Reliable across re-mounts because each
// element controls its own animation instead of relying on parent orchestration.
function Item({ children, index = 0, className = "" }) {
  const reduce = useReducedMotion();
  if (reduce) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay: 0.15 + index * 0.14, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

// Accessible DOM demonstration of a single deposit being split.
// $2,400 arrives, 25 percent (600) is routed to taxes, 1,800 stays.
export default function DepositSplitMock() {
  const reduce = useReducedMotion();

  return (
    <div
      className="relative w-full max-w-md"
      role="figure"
      aria-label="A 2,400 dollar deposit is detected. 25 percent, which is 600 dollars, is routed to taxes. 1,800 dollars stays in your account."
    >
      {/* Floating status badge */}
      <div className="absolute -right-3 -top-5 z-20 flex items-center gap-2 rounded-full border border-etap-mint-300 bg-white px-4 py-2 shadow-etap-lg">
        <span className="relative flex h-2.5 w-2.5">
          {!reduce && (
            <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-etap-mint-500" />
          )}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-etap-mint-600" />
        </span>
        <span className="font-display text-sm font-bold text-etap-deep-700">You are covered</span>
      </div>

      <div className="grain relative overflow-hidden rounded-3xl border border-etap-deep-100 bg-white p-6 shadow-etap-xl sm:p-7">
        {/* Deposit card */}
        <Item index={0} className="rounded-2xl border border-etap-primary-100 bg-etap-primary-50 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-etap-primary text-white">
                <Wallet className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-etap-primary-600">Deposit detected</p>
                <p className="text-sm text-etap-deep-600">Freelance invoice</p>
              </div>
            </div>
            <p className="font-display text-2xl font-bold text-etap-deep-800">$2,400</p>
          </div>
        </Item>

        {/* Split connector */}
        <Item index={1} className="flex items-center justify-center py-3">
          <span className="flex items-center gap-2 rounded-full bg-etap-deep-50 px-3 py-1 text-xs font-semibold text-etap-deep-600">
            <ArrowDownRight className="h-4 w-4" aria-hidden="true" />
            Split at your chosen 25%
          </span>
        </Item>

        {/* Routed row */}
        <Item index={2} className="mb-3 flex items-center justify-between rounded-2xl border border-etap-mint-300 bg-etap-mint-100 p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-etap-deep text-white">
              <Landmark className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <p className="font-display text-sm font-bold text-etap-deep-800">Routed to taxes</p>
              <p className="text-xs text-etap-deep-600">Federal and state, sent to authorities</p>
            </div>
          </div>
          <p className="font-display text-lg font-bold text-etap-deep-800">$600</p>
        </Item>

        {/* Stays row */}
        <Item index={3} className="flex items-center justify-between rounded-2xl border border-etap-deep-100 bg-white p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-etap-primary-100 text-etap-primary-600">
              <Wallet className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <p className="font-display text-sm font-bold text-etap-deep-800">Stays in your account</p>
              <p className="text-xs text-etap-deep-500">Yours to spend or save</p>
            </div>
          </div>
          <p className="font-display text-lg font-bold text-etap-deep-800">$1,800</p>
        </Item>

        <Item index={4}>
          <p className="mt-5 flex items-center gap-2 text-xs font-medium text-etap-deep-500">
            <ShieldCheck className="h-4 w-4 text-etap-primary" aria-hidden="true" />
            ETAP never holds your money. It moves from your account to tax authorities.
          </p>
        </Item>
      </div>
    </div>
  );
}
